// ============================================================================
// fileName.ts — хелперы имени/расширения файла (SSoT).
// ============================================================================

// fileExt — расширение файла без точки, нижний регистр ('' если нет).
export function fileExt(name: string): string {
  const i = (name || '').lastIndexOf('.');
  if (i < 0 || i === name.length - 1) return '';
  return name.slice(i + 1).toLowerCase();
}

// baseName — имя без расширения.
export function baseName(name: string): string {
  const i = (name || '').lastIndexOf('.');
  if (i <= 0) return name || '';
  return name.slice(0, i);
}

// isImageName/isAudioName/isVideoName — быстрые проверки по расширению.
export function isImageName(name: string): boolean {
  return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico', 'avif'].includes(fileExt(name));
}
export function isAudioName(name: string): boolean {
  return ['mp3', 'wav', 'ogg', 'opus', 'm4a', 'aac', 'flac', 'webm'].includes(fileExt(name));
}
export function isVideoName(name: string): boolean {
  return ['mp4', 'webm', 'mov', 'mkv', 'avi', 'm4v'].includes(fileExt(name));
}

// ============================================================================
// mediaKind — ЕДИНЫЙ SSoT классификации вложений/файлов (SRP).
// MIME (серверный file_type из «Файлов») ПЕРВИЧЕН: webm бывает и голосовым
// (audio/webm), и видео (video/webm) — по расширению не различить.
// Расширение — только запасной признак (нет mime).
// ============================================================================

export type MediaKind = 'image' | 'audio' | 'video' | 'file';

// ============================================================================
// isVoiceMessage — голосовое сообщение ОТДЕЛЬНО от обычного аудио.
// Голосовые записывает сам чат (MediaRecorder): имя `voice-…`, MIME
// audio/webm (opus). mp3/m4a/ogg/flac — обычное аудио (плеер с waveform),
// НЕ голосовое. MIME первичен; расширение webm без MIME — запись.
// ============================================================================

export function isVoiceMessage(name: string, mime?: string): boolean {
  const m = (mime || '').toLowerCase().split(';')[0].trim();
  if (m === 'audio/webm' || m === 'audio/opus' || m === 'audio/x-opus+ogg') return true;
  if (m && m !== 'application/octet-stream') return false;
  const base = baseName(name).toLowerCase();
  if (base.startsWith('voice-') || base.startsWith('голосовое-')) return true;
  return fileExt(name) === 'webm';
}

export function mediaKind(name: string, mime?: string): MediaKind {
  const m = (mime || '').toLowerCase().trim();
  if (m.startsWith('image/')) return 'image';
  if (m.startsWith('audio/')) return 'audio';
  if (m.startsWith('video/')) return 'video';
  // MIME нет/неизвестен — расширение. webm без MIME неоднозначен: запись
  // голосового (`voice-…`) — аудио, остальные webm — ВИДЕО (так их и играет
  // браузер); сервер обычно присылает MIME (audio/webm | video/webm).
  const ext = fileExt(name);
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico', 'avif'].includes(ext)) return 'image';
  if (['mp3', 'wav', 'ogg', 'opus', 'm4a', 'aac', 'flac'].includes(ext)) return 'audio';
  if (ext === 'webm') return isVoiceMessage(name) ? 'audio' : 'video';
  if (['mp4', 'mov', 'mkv', 'avi', 'm4v'].includes(ext)) return 'video';
  return 'file';
}
